import type { LayoutLine } from "./SplitMeasuredTokens.ts";
import type { ISplitOptions } from "../../models/text/ISplitOptions.ts";
import type { FitContext } from "./FitFontSizeCalculator.ts";
import { FitFontSizeCalculator } from "./FitFontSizeCalculator.ts";
import { SplitOptionsPredicates } from "./SplitOptionsPredicates.ts";

/**
 * DOM-measuring counterpart to `FitFontSizeCalculator`: builds the `FitContext`
 * from a live element, and corrects the calculator's provisional fit-font-size
 * against real rendered widths (browsers don't scale glyph width exactly
 * linearly with font size — hinting and subpixel rounding break that
 * assumption enough to matter for `nowrap`/flex-wrap layouts).
 */
export class FitFontSizeRenderer {
  /**
   * `elementContentWidth` is computed by the caller (`StringSplit`), which
   * already needs the same block-container-aware width logic for non-fit
   * splitting — passed in rather than duplicated here.
   */
  static getFitContext(
    layoutLines: LayoutLine[],
    element: HTMLElement,
    elementContentWidth: number,
  ): FitContext | null {
    const cs = window.getComputedStyle(element);
    const currentFontSize = parseFloat(cs.fontSize);
    if (!currentFontSize) return null;
    if (elementContentWidth <= 0) return null;

    let widestLineWidth = 0;
    for (const line of layoutLines) {
      const w = line.fitWidth ?? line.rect.width;
      if (w > widestLineWidth) widestLineWidth = w;
    }
    if (widestLineWidth <= 0) return null;

    let contentWidth = elementContentWidth;
    if (Math.abs(elementContentWidth - widestLineWidth) < 2 && element.parentElement) {
      const pcs = window.getComputedStyle(element.parentElement);
      const parentContentWidth =
        element.parentElement.clientWidth -
        (parseFloat(pcs.paddingLeft) || 0) -
        (parseFloat(pcs.paddingRight) || 0);
      if (parentContentWidth > elementContentWidth) {
        contentWidth = parentContentWidth;
      }
    }

    const lineHeightPx = parseFloat(cs.lineHeight) || 0;
    return { currentFontSize, contentWidth, lineHeightPx };
  }

  static refineFitFontSize(
    element: HTMLElement,
    fragment: DocumentFragment,
    extraProps: Map<string, string>,
    layoutLines: LayoutLine[],
    options: ISplitOptions,
    fitContext: FitContext,
  ): void {
    const hasLineSplit = SplitOptionsPredicates.hasLineDrivenSplit(options);
    const hasCharSplit = SplitOptionsPredicates.hasCharDrivenSplit(options);

    const originalHtml = element.innerHTML;

    try {
      element.innerHTML = "";
      element.appendChild(fragment.cloneNode(true));

      if (hasLineSplit) {
        const renderedLines = Array.from(element.querySelectorAll(".-s-line")) as HTMLElement[];
        const resultLines = Array.from(fragment.querySelectorAll(".-s-line")) as HTMLElement[];

        renderedLines.forEach((renderedLine, index) => {
          const resultLine = resultLines[index];
          if (!resultLine) return;

          const provisional = parseFloat(resultLine.style.getPropertyValue("--fit-font-size"));
          if (!provisional) return;

          const corrected = FitFontSizeRenderer.solveRenderedFitFontSize(
            renderedLine,
            fitContext.currentFontSize,
            provisional,
            fitContext.contentWidth,
            hasCharSplit,
          );
          if (!corrected) return;

          const finalFontSize = FitFontSizeRenderer.shrinkToFit(
            renderedLine,
            hasCharSplit,
            Math.floor(corrected),
            fitContext.contentWidth,
          );
          layoutLines[index].fitFontSize = finalFontSize;
          resultLine.style.setProperty("--fit-font-size", String(finalFontSize));
        });
      } else {
        const provisional = parseFloat(extraProps.get("--fit-font-size") ?? "");
        if (!provisional) return;

        const corrected = FitFontSizeRenderer.solveRenderedFitFontSize(
          element,
          fitContext.currentFontSize,
          provisional,
          fitContext.contentWidth,
          hasCharSplit,
        );
        if (!corrected) return;

        const finalFontSize = FitFontSizeRenderer.shrinkToFit(
          element,
          hasCharSplit,
          Math.floor(corrected),
          fitContext.contentWidth,
        );
        extraProps.set("--fit-font-size", String(finalFontSize));

        const { lineHeightPx, currentFontSize, contentWidth } = fitContext;
        if (lineHeightPx > 0 && currentFontSize > 0) {
          const textHeightAtFit = lineHeightPx * (finalFontSize / currentFontSize);
          if (textHeightAtFit > 0) {
            extraProps.set("--fit-scale-y", String(window.innerHeight / textHeightAtFit));
            extraProps.set("--fit-aspect-ratio", String(contentWidth / textHeightAtFit));
          }
        }
      }
    } finally {
      element.innerHTML = originalHtml;
    }
  }

  private static solveRenderedFitFontSize(
    scope: HTMLElement,
    currentFontSize: number,
    provisionalFontSize: number,
    targetWidth: number,
    applyToChars: boolean,
  ): number | null {
    if (!Number.isFinite(currentFontSize) || !Number.isFinite(provisionalFontSize)) return null;
    if (currentFontSize <= 0 || provisionalFontSize <= 0) return null;

    const baselineWidth = FitFontSizeRenderer.measureScopeAtFontSize(
      scope,
      applyToChars,
      currentFontSize,
    );
    const trialWidth =
      Math.abs(provisionalFontSize - currentFontSize) < 0.01
        ? baselineWidth
        : FitFontSizeRenderer.measureScopeAtFontSize(scope, applyToChars, provisionalFontSize);

    return FitFontSizeCalculator.solve(
      currentFontSize,
      provisionalFontSize,
      targetWidth,
      baselineWidth,
      trialWidth,
    );
  }

  /**
   * The linear solve in `solveRenderedFitFontSize` assumes width scales
   * exactly with font size, which browsers don't guarantee (hinting,
   * subpixel rounding). That can leave the solved size a fraction of a
   * pixel too wide, which `nowrap`/flex-wrap layouts render as a dropped
   * character instead of harmless overflow. Verify the candidate size
   * against a real measurement and step down until it actually fits.
   */
  private static shrinkToFit(
    scope: HTMLElement,
    applyToChars: boolean,
    candidateFontSize: number,
    targetWidth: number,
  ): number {
    // A width landing exactly on targetWidth still trips flex-wrap in some
    // browsers (subpixel rounding across chars/margins can round either way
    // at an exact boundary), so require strictly under it, not <=.
    const safetyMargin = 1;
    const maxSteps = 6;
    let fontSize = candidateFontSize;
    for (let i = 0; i < maxSteps && fontSize > 0; i++) {
      const width = FitFontSizeRenderer.measureScopeAtFontSize(scope, applyToChars, fontSize);
      if (width <= targetWidth - safetyMargin) break;
      fontSize -= 1;
    }
    return fontSize;
  }

  private static measureScopeAtFontSize(
    scope: HTMLElement,
    applyToChars: boolean,
    fontSize: number,
  ): number {
    if (applyToChars) {
      const chars = Array.from(scope.querySelectorAll(".-s-char")) as HTMLElement[];
      const restore = chars.map((char) => char.style.fontSize);
      chars.forEach((char) => {
        char.style.fontSize = `${fontSize}px`;
      });

      scope.offsetWidth;
      const width = FitFontSizeRenderer.measureCharScopeWidth(scope);

      chars.forEach((char, index) => {
        char.style.fontSize = restore[index];
      });
      return width;
    }

    const previous = scope.style.fontSize;
    scope.style.fontSize = `${fontSize}px`;
    scope.offsetWidth;
    const width = FitFontSizeRenderer.measureContentWidth(scope);
    scope.style.fontSize = previous;
    return width;
  }

  private static measureCharScopeWidth(scope: HTMLElement): number {
    let width = 0;

    const chars = Array.from(scope.querySelectorAll(".-s-char")) as HTMLElement[];
    chars.forEach((char) => {
      const rect = char.getBoundingClientRect();
      const style = window.getComputedStyle(char);
      width +=
        rect.width + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
    });

    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let textNode: Node | null = walker.nextNode();
    while (textNode) {
      const parent = textNode.parentElement;
      const inChar = !!parent?.closest(".-s-char");
      const inLeafElement =
        !!parent &&
        !parent.classList.contains("-s-char") &&
        !parent.classList.contains("-s-word") &&
        !parent.classList.contains("-s-line") &&
        !parent.querySelector(".-s-char, .-s-word, .-s-line");

      if (!inChar && !inLeafElement && textNode.textContent?.length) {
        const range = document.createRange();
        range.selectNodeContents(textNode);
        width += range.getBoundingClientRect().width;
      }

      textNode = walker.nextNode();
    }

    const leafElements = Array.from(scope.querySelectorAll("*")).filter((node) => {
      const el = node as HTMLElement;
      return (
        !el.classList.contains("-s-char") &&
        !el.classList.contains("-s-word") &&
        !el.classList.contains("-s-line") &&
        !el.querySelector(".-s-char, .-s-word, .-s-line")
      );
    }) as HTMLElement[];

    leafElements.forEach((leaf) => {
      width += leaf.getBoundingClientRect().width;
    });

    return width;
  }

  private static measureContentWidth(scope: HTMLElement): number {
    if (!scope.childNodes.length) {
      return scope.getBoundingClientRect().width;
    }

    const range = document.createRange();
    range.selectNodeContents(scope);
    return range.getBoundingClientRect().width;
  }
}
